import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import {
  EN,
  DE,
  FR,
  ES,
} from 'services/languages/constants'
import { getUserLanguagePrimary } from 'services/user'
import { MODAL_TYPES } from 'components/Modal'
import { TYPE_ANNOUNCEMENT_MODAL } from 'services/dialog'
import { RESOLVER_TYPE_NOT_FOUND } from 'services/resolver/types'
import {
  isAccountPage,
  isSharePage,
  isPortal,
  isCart,
  isCartAccessDenied,
  isPlans,
  isGift,
  isGo,
  isGetStarted,
  isFullPlayer,
} from 'services/url'
import { isEntitled } from 'services/subscription'
import { variationShouldRender } from 'services/testarossa'

const ANNOUNCEMENT_ACTIVATION_TIME = 2000

function getStaticText (language) {
  const staticText = {
    [EN]: {
      heading: 'Introducing Gaia Sages',
      description: 'Sages is a conversational tool now available to select Gaia members! Ask questions, request practices tailored to your personal goals, and explore conscious and esoteric concepts in an easy fluid new medium. Access Sages in the navigation, or directly on your Gaia Home page.  Try it out and let us know what you think!',
      button: null,
    },
    [DE]: {
      heading: 'Gaia Sages eingeführt',
      description: 'Gaia Sages ist ein konversationsfähiges Tool, das jetzt für ausgewählte Gaia-Mitglieder verfügbar ist! Entwickelt in Zusammenarbeit mit Robert Grant, kannst du Gaia Sages im Nav, oder direkt auf deiner Gaia-Startseite nutzen.  Probieren Sie es aus und lassen Sie uns wissen, was Sie dabei denken!',
      button: null,
    },
    [FR]: {
      heading: 'Gaia Sages introduit',
      description: 'Gaia Sages est un outil conversationnel maintenant disponible pour les membres de Gaia ! Développé en collaboration avec Robert Grant, vous pouvez accéder à Gaia Sages dans la navigation, ou directement sur votre page d\'accueil de Gaia.  Essayez-le et dites-nous ce que vous en pensez !',
      button: null,
    },
    [ES]: {
      heading: 'Gaia Sages introducido',
      description: 'Gaia Sages es una herramienta conversacional ahora disponible para miembros de Gaia! Desarrollado en colaboración con Robert Grant, puedes acceder a Gaia Sages en la navegación, o directamente en tu página de inicio de Gaia.  ¡Pruébalo y díganos lo que piensas!',
      button: null,
    },
  }
  const localizedText = staticText[language] || staticText[EN]
  return Map(localizedText)
}

function Announcement (props) {
  const {
    isTourActive,
    shouldHideAnnouncement,
    setFeatureTrackingDataPersistent,
    auth,
    language,
    renderModal,
    dismissModal,
    view,
    resolver,
    testarossa,
  } = props

  const staticText = getStaticText(language)

  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))
  const isUserEnrolled = testarossa.get('initialized')
    && (variationShouldRender({ campaign: 'CP-8', variation: 1 })
      || variationShouldRender({ campaign: 'CP-11', variation: 1 }))

  const shouldShowAnnouncement = useMemo(() => {
    const path = resolver.getIn(['data', 'path'])
    const query = resolver.getIn(['data', 'query'])

    /**
     * do not show announcement if user is not enrolled into CP-8 test
     */
    if (!isUserEnrolled) {
      return false
    }
    /**
     * do not show announcement if language is not English
     */
    if (language !== EN) {
      return false
    }
    /**
     * do not show announcement if user already seen it
     * or tour is active or interstitial is active
     * or not entitled
     */
    if (shouldHideAnnouncement || isTourActive || view || !isUserEntitled) {
      return false
    }
    /**
     * do not show announcemnt if the page is not found
     */
    if (resolver.getIn(['data', 'type']) === RESOLVER_TYPE_NOT_FOUND) {
      return false
    }
    /**
     * do not show announcemnt for the specific pages
     */
    if (
      isAccountPage(path)
      || isSharePage(path)
      || isPortal(path)
      || isCart(path)
      || isCartAccessDenied(path)
      || isPlans(path)
      || isGift(path)
      || isGo(path)
      || isGetStarted(path)
      || isFullPlayer(query)
    ) {
      return false
    }
    return true
  }, [
    resolver,
    shouldHideAnnouncement,
    isTourActive,
    view,
    isUserEntitled,
    language,
    isUserEnrolled,
  ])

  const handleCloseClick = () => {
    handleModalDismiss()
    dismissModal(TYPE_ANNOUNCEMENT_MODAL)
  }

  const handleModalDismiss = () => {
    setFeatureTrackingDataPersistent({
      data: Map({ architectIntroModal: true }),
      auth,
    })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowAnnouncement) {
        renderModal(TYPE_ANNOUNCEMENT_MODAL, {
          type: MODAL_TYPES.ROYAL_VIDEO,
          onDismiss: handleModalDismiss,
          onCloseClick: handleCloseClick,
          staticText,
        })
      }
    }, ANNOUNCEMENT_ACTIVATION_TIME)
    return () => {
      clearTimeout(timer)
    }
  }, [shouldShowAnnouncement])

  return null
}

Announcement.propTypes = {
  shouldHideAnnouncement: PropTypes.bool,
  isTourActive: PropTypes.bool,
  language: PropTypes.string.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  view: PropTypes.string,
  checkoutComplete: PropTypes.bool,
}

export default connectRedux(
  (state) => ({
    shouldHideAnnouncement: state.featureTracking.getIn(['data', 'architectIntroModal']),
    isTourActive: state.tour.get('isActive'),
    language: getUserLanguagePrimary(state.user) || EN,
    auth: state.auth,
    view: state.interstitial.get('view'),
    resolver: state.resolver,
    testarossa: state.testarossa,
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setFeatureTrackingDataPersistent: actions.featureTracking.setFeatureTrackingDataPersistent,
      renderModal: actions.dialog.renderModal,
      dismissModal: actions.dialog.dismissModal,
    }
  },
)(Announcement)
